import { PromptRefinementAngle } from '@api';
import { Button, Card } from '@design';
import { useRows } from '@terminal/components/chat';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './PromptAngleCard.module.scss';
import { getLensColorVar } from './utils/getLensColorVar';

export interface PromptAngleCardProps {
  angle: PromptRefinementAngle;
  isOpen: boolean;
  onSelectPrompt: (prompt: string) => void;
  onSuggestionEdited?: () => void;
}

export const PromptAngleCard = ({
  angle,
  isOpen,
  onSelectPrompt,
  onSuggestionEdited,
}: PromptAngleCardProps) => {
  const { t } = useTranslation('chat');
  const [isEditing, setIsEditing] = useState(false);
  const [displayedPrompt, setDisplayedPrompt] = useState(angle.prompt);
  const [draftPrompt, setDraftPrompt] = useState(angle.prompt);
  const editInputRef = useRef<HTMLTextAreaElement>(null);
  // useRows' measure effect only fires when its `value` changes, so passing an
  // empty string while not editing forces it to re-measure when the textarea
  // mounts on edit-mode entry. textAreaLineHeight matches typography-v2-body-sm.
  const { rows, updateRows } = useRows({
    value: isEditing ? draftPrompt : '',
    textAreaRef: editInputRef,
    textAreaLineHeight: 20,
    compactMode: true,
  });

  useEffect(() => {
    setIsEditing(false);
    setDisplayedPrompt(angle.prompt);
    setDraftPrompt(angle.prompt);
  }, [isOpen, angle.prompt]);

  const setEditInputRef = (el: HTMLTextAreaElement | null) => {
    editInputRef.current = el;

    if (!el) {
      return;
    }

    el.focus();
    const end = el.value.length;
    el.setSelectionRange(end, end);
  };

  const handleStartEdit = () => {
    setDraftPrompt(displayedPrompt);
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setDraftPrompt(displayedPrompt);
    setIsEditing(false);
  };

  const handleApplyEdit = () => {
    const trimmed = draftPrompt.trim();

    if (!trimmed || trimmed === displayedPrompt.trim()) {
      return;
    }

    setDisplayedPrompt(trimmed);
    setDraftPrompt(trimmed);
    setIsEditing(false);
    onSuggestionEdited?.();
  };

  const trimmedDraft = draftPrompt.trim();
  const isUpdateDisabled =
    !trimmedDraft || trimmedDraft === displayedPrompt.trim();

  return (
    <Card className={styles.card}>
      <div className={styles.cardHeader}>
        <div>
          <p
            className={styles.cardEyebrow}
            style={{ color: getLensColorVar(angle.lens) }}
          >
            {angle.lens}
          </p>
          <h5 className={styles.cardTitle}>{angle.label}</h5>
        </div>
        <span className={styles.cardMeta}>{angle.depth}</span>
      </div>
      {isEditing ? (
        <textarea
          ref={setEditInputRef}
          className={styles.editInput}
          value={draftPrompt}
          onChange={e => {
            updateRows(e);
            setDraftPrompt(e.target.value);
          }}
          rows={rows}
          aria-label={t('chat:promptRefinement.editPrompt')}
          data-testid="prompt-refinement-angle-edit-input"
        />
      ) : (
        <p className={styles.cardPrompt}>{displayedPrompt}</p>
      )}
      <div className={styles.cardAction}>
        {isEditing ? (
          <>
            <Button
              type="button"
              variant="tertiary"
              size="small"
              label={t('chat:promptRefinement.cancel')}
              onClick={handleCancelEdit}
            />
            <Button
              type="button"
              variant="secondary"
              size="small"
              label={t('chat:promptRefinement.update')}
              onClick={handleApplyEdit}
              disabled={isUpdateDisabled}
            />
          </>
        ) : (
          <>
            <Button
              type="button"
              variant="secondary"
              size="small"
              label={t('chat:promptRefinement.editPrompt')}
              onClick={handleStartEdit}
            />
            <Button
              type="button"
              variant="primary"
              size="small"
              label={t('chat:promptRefinement.usePrompt')}
              onClick={() => onSelectPrompt(displayedPrompt)}
            />
          </>
        )}
      </div>
    </Card>
  );
};
